type ContextValue = string | number | boolean | null

// tests/client-log-context-limits-contract.test.ts verifies parity with the API context limits.
const MAX_KEYS = 25
const MAX_BYTES = 8192
const TRUNCATION_KEY = 'contextTruncated'
const VALUE_TRUNCATION_SUFFIX = '…[truncated]'
const MAX_SHRINK_ITERATIONS = 500

// Bounds diagnostic context to retain entries during API ingestion.
const IDENTIFYING_KEYS: readonly string[] = [
  'name',
  'message',
  'code',
  'status',
  'requestId',
  'count'
]

function byteLength(value: string): number {
  return new TextEncoder().encode(value).length
}

function serializedByteSize(
  value: ContextValue | Readonly<Record<string, ContextValue>> | undefined
): number {
  const isContext = typeof value === 'object' && value !== null
  const values = isContext ? Object.values(value) : [value]
  const numericCount = values.filter((item) => typeof item === 'number').length

  // PHP's shortest JSON float format can use two more bytes per numeric value.
  return byteLength(JSON.stringify(value)) + numericCount * 2
}

function isWithinBudget(context: Readonly<Record<string, ContextValue>>): boolean {
  return Object.keys(context).length <= MAX_KEYS && serializedByteSize(context) <= MAX_BYTES
}

// Non-identifying keys are kept smallest-value-first, so the largest (least identifying) drop first.
function selectKeysWithinBudget(
  context: Readonly<Record<string, ContextValue>>
): Record<string, ContextValue> {
  const budget = MAX_KEYS - 1
  const identifying = IDENTIFYING_KEYS.filter((key) => Object.hasOwn(context, key))
  const others = Object.keys(context).filter((key) => !identifying.includes(key))
  const keptOthers = [...others]
    .sort((a, b) => serializedByteSize(context[a]) - serializedByteSize(context[b]))
    .slice(0, Math.max(0, budget - identifying.length))

  const kept: Record<string, ContextValue> = {}
  for (const key of [...identifying, ...keptOthers]) {
    kept[key] = context[key] as ContextValue
  }
  return kept
}

function longestStringValueKey(
  context: Readonly<Record<string, ContextValue>>,
  exhausted: ReadonlySet<string>
): string | null {
  let longestKey: string | null = null
  let longestBytes = -1
  for (const [key, value] of Object.entries(context)) {
    if (typeof value !== 'string' || exhausted.has(key)) continue
    const bytes = byteLength(value)
    if (bytes > longestBytes) {
      longestKey = key
      longestBytes = bytes
    }
  }
  return longestKey
}

function shrinkToFit(context: Record<string, ContextValue>): Record<string, ContextValue> {
  const working = { ...context }
  const exhausted = new Set<string>()

  for (let iteration = 0; iteration < MAX_SHRINK_ITERATIONS; iteration += 1) {
    if (serializedByteSize(working) <= MAX_BYTES) break

    const key = longestStringValueKey(working, exhausted)
    if (key === null) break

    const current = working[key] as string
    const nextLength = Math.floor(current.length * 0.75)
    const shortened = current.slice(0, nextLength).toWellFormed() + VALUE_TRUNCATION_SUFFIX
    if (shortened.length >= current.length) {
      exhausted.add(key)
      continue
    }
    working[key] = shortened
  }

  return working
}

function dropLargestNonIdentifyingKey(
  context: Record<string, ContextValue>
): Record<string, ContextValue> | null {
  const droppable = Object.keys(context).filter(
    (key) => !IDENTIFYING_KEYS.includes(key) && key !== TRUNCATION_KEY
  )
  if (droppable.length === 0) return null

  const largest = droppable.reduce((a, b) =>
    serializedByteSize(context[a]) >= serializedByteSize(context[b]) ? a : b
  )
  const { [largest]: _dropped, ...rest } = context
  return rest
}

interface BoundedLogContext {
  readonly context: Record<string, ContextValue>
  readonly truncated: boolean
}

// Reduces an oversized context to fit mealz-api's limits; sets `contextTruncated: true` when it does.
export function boundLogContext(
  context: Readonly<Record<string, ContextValue>>
): BoundedLogContext {
  if (isWithinBudget(context)) return { context: { ...context }, truncated: false }

  let working: Record<string, ContextValue> = {
    ...selectKeysWithinBudget(context),
    [TRUNCATION_KEY]: true
  }
  working = shrinkToFit(working)

  while (serializedByteSize(working) > MAX_BYTES) {
    const dropped = dropLargestNonIdentifyingKey(working)
    if (dropped === null) break
    working = dropped
  }

  return { context: working, truncated: true }
}
